import { isBotId, type Replay } from "@69ly/shared";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cs2Icon, WeaponIcon } from "../demo/cs2-icons.tsx";
import { duelGrid, duelsOf, duelTotals, type Duel } from "../demo/duels.ts";
import { buyMatchups, economyOf, hasBuys, type EconomyRound } from "../demo/economy.ts";
import type { RoundIndex } from "../demo/scene.ts";

/**
 * Two analysis tabs over the whole map: Duels (openings, who killed whom, each duel in
 * detail, trades) and Economy (buys round by round, money, results by buy).
 */

const SIDE_CLASS = { t: "side-t", ct: "side-ct" } as const;

/** The width of a box, followed as it changes: charts fit it rather than scroll. */
function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => el.clientWidth > 0 && setWidth(el.clientWidth);
    read();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(read);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  return [ref, width] as const;
}

/** Round numbers under a chart: all of them when there is room, else every fifth. */
const labelled = (i: number, step: number) => step >= 22 || i === 0 || (i + 1) % 5 === 0;
const money = (n: number) => `$${n.toLocaleString("en-US")}`;
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : "–");
const clock = (s: number) => {
  const v = Math.max(0, Math.floor(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

interface Common {
  replay: Replay;
  round: RoundIndex;
  numbers: number[];
  /** Side A of the match (our team when we played), shown first. */
  first: 0 | 1;
  nameOf: (team: 0 | 1) => string;
  onGo: (round: number, seconds: number) => void;
}

/** A player's side in a given round. */
const sideIn = (r: Replay, player: number, round: number): "t" | "ct" => (r.players[player]?.team === r.rounds[round]?.tTeam ? "t" : "ct");

function Who({ r, i, round, numbers }: { r: Replay; i: number; round: number; numbers: number[] }) {
  return (
    <span className={`kill-who ${SIDE_CLASS[sideIn(r, i, round)]}`}>
      <span className="demo-num-mini">{numbers[i]}</span>
      {r.players[i]?.name}
    </span>
  );
}

export function DuelsPanel({ replay, round, numbers, first, nameOf, onGo, part }: Common & { part?: "players" | "list" }) {
  const { t } = useTranslation();
  // In the theater the players' numbers and the duels go in two panels.
  const show = (p: "players" | "list") => !part || part === p;
  const duels = useMemo(() => duelsOf(replay), [replay]);
  const totals = useMemo(() => duelTotals(replay, duels), [replay, duels]);
  const grid = useMemo(() => duelGrid(replay, duels), [replay, duels]);
  const [scope, setScope] = useState<"round" | "openings" | "all">("round");
  const list = scope === "round" ? duels.filter((d) => d.round === round.index) : scope === "openings" ? duels.filter((d) => d.opening) : duels;
  // Bots standing in for a player who left are not listed.
  const teamOf = (team: 0 | 1) => replay.players.map((_, i) => i).filter((i) => replay.players[i].team === team && !isBotId(replay.players[i].steamId));
  const a = teamOf(first);
  const b = teamOf((1 - first) as 0 | 1);

  return (
    <div className="demo-duels">
      {show("players") && (
        <section aria-labelledby="duels-players">
          <h3 id="duels-players" className="eyebrow">
            {t("demos.duels.players")}
          </h3>
          <div className="table-scroll">
            <table className="stats-table">
              <thead>
                <tr>
                  <th scope="col">{t("demos.viewer.cols.player")}</th>
                  <th scope="col" className="num" title={t("demos.duels.kdHelp")}>
                    {t("demos.duels.kd")}
                  </th>
                  <th scope="col" className="num" title={t("demos.duels.openingHelp")}>
                    {t("demos.duels.opening")}
                  </th>
                  <th scope="col" className="num" title={t("demos.duels.tradesHelp")}>
                    {t("demos.duels.trades")}
                  </th>
                  <th scope="col" className="num" title={t("demos.duels.untradedHelp")}>
                    {t("demos.duels.untraded")}
                  </th>
                  <th scope="col" className="num" title={t("demos.duels.hsHelp")}>
                    {t("demos.duels.hs")}
                  </th>
                </tr>
              </thead>
              {[a, b].map((team, ti) => (
                <tbody key={ti}>
                  {team.map((i) => {
                    const s = totals[i];
                    return (
                      <tr key={i}>
                        <th scope="row">
                          <Who r={replay} i={i} round={round.index} numbers={numbers} />
                        </th>
                        <td className="num mono">
                          {s.kills}–{s.deaths}
                        </td>
                        <td className="num mono">
                          {s.openingWon}–{s.openingLost}
                        </td>
                        <td className="num mono">{s.trades}</td>
                        <td className="num mono">
                          {s.untraded}/{s.deaths}
                        </td>
                        <td className="num mono">{pct(s.headshots, s.kills)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        </section>
      )}

      {show("players") && (
        <section aria-labelledby="duels-grid">
          <h3 id="duels-grid" className="eyebrow">
            {t("demos.duels.grid")}
          </h3>
          <p className="muted small">{t("demos.duels.gridHelp", { a: nameOf(first), b: nameOf((1 - first) as 0 | 1) })}</p>
          <div className="table-scroll">
            <table className="stats-table duel-grid">
              <thead>
                <tr>
                  <td />
                  {b.map((j) => (
                    <th key={j} scope="col" className="num" title={replay.players[j].name}>
                      <span className="duel-grid-head">{replay.players[j].name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {a.map((i) => (
                  <tr key={i}>
                    <th scope="row">
                      <Who r={replay} i={i} round={round.index} numbers={numbers} />
                    </th>
                    {b.map((j) => {
                      const won = grid.get(i)?.get(j) ?? 0;
                      const lost = grid.get(j)?.get(i) ?? 0;
                      const tone = won > lost ? "is-up" : lost > won ? "is-down" : "";
                      return (
                        <td key={j} className={`num mono duel-cell ${tone}`} title={t("demos.duels.cell", { a: replay.players[i].name, b: replay.players[j].name, won, lost })}>
                          {won || lost ? `${won}–${lost}` : "·"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {show("list") && (
        <section aria-labelledby="duels-list">
          <div className="duels-list-head">
            <h3 id="duels-list" className="eyebrow">
              {t("demos.duels.list")}
            </h3>
            <div className="segmented segmented-small" role="group" aria-label={t("demos.duels.which")}>
              {(["round", "openings", "all"] as const).map((s) => (
                <button key={s} type="button" className="seg" aria-pressed={scope === s} onClick={() => setScope(s)}>
                  {t(`demos.duels.scope.${s}`, { n: round.round.number })}
                </button>
              ))}
            </div>
          </div>
          {list.length === 0 ? (
            <p className="muted">{t("demos.duels.none")}</p>
          ) : (
            <ol className="duel-list">
              {list.map((d) => (
                <DuelItem key={`${d.kill.tick}-${d.victim}`} d={d} replay={replay} numbers={numbers} showRound={scope !== "round"} onGo={onGo} />
              ))}
            </ol>
          )}
        </section>
      )}
    </div>
  );
}

function DuelItem({ d, replay, numbers, showRound, onGo }: { d: Duel; replay: Replay; numbers: number[]; showRound: boolean; onGo: (round: number, s: number) => void }) {
  const { t } = useTranslation();
  const tags = [
    d.opening && t("demos.duels.tag.opening"),
    d.trade && t("demos.duels.tag.trade"),
    d.traded ? t("demos.duels.tag.traded") : t("demos.duels.tag.untraded"),
    d.victimFlashed && t("demos.duels.tag.flashed"),
  ].filter(Boolean) as string[];
  const first = d.firstHit < 0 ? null : d.firstHit === d.killer ? t("demos.duels.firstKiller") : t("demos.duels.firstVictim");
  return (
    <li>
      <button type="button" className="duel-item" onClick={() => onGo(d.round, Math.max(0, d.at - 3))} title={t("demos.duels.watch")}>
        <span className="mono small muted duel-when">
          {showRound && `R${replay.rounds[d.round].number} · `}
          {clock(d.at)}
        </span>
        <span className="duel-who">
          <Who r={replay} i={d.killer} round={d.round} numbers={numbers} />
          <WeaponIcon weapon={d.kill.weapon} className="kill-weapon" />
          {d.kill.headshot && <Cs2Icon name="headshot" label={t("demos.viewer.headshot")} className="kill-mark" />}
          {d.kill.wallbang && <Cs2Icon name="wallbang" label={t("demos.viewer.wallbang")} className="kill-mark" />}
          {d.kill.smoke && <Cs2Icon name="through_smoke" label={t("demos.viewer.throughSmoke")} className="kill-mark" />}
          <Who r={replay} i={d.victim} round={d.round} numbers={numbers} />
        </span>
        <span className="duel-facts small muted">
          {[
            first,
            t("demos.duels.damage", { dealt: d.damageDealt, taken: d.damageTaken }),
            d.timeToKill > 0 ? t("demos.duels.ttk", { s: d.timeToKill.toFixed(2) }) : t("demos.duels.oneTap"),
            t("demos.duels.distance", { m: Math.round(d.distance) }),
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
        {tags.length > 0 && (
          <span className="duel-tags">
            {tags.map((x) => (
              <span key={x} className="chip chip-small">
                {x}
              </span>
            ))}
          </span>
        )}
      </button>
    </li>
  );
}

// ---------------------------------------------------------------- economy

export function EconomyPanel({ replay, round, numbers, first, nameOf, onGo, part }: Common & { part?: "map" | "round" }) {
  const { t } = useTranslation();
  // In the theater the map's economy and this round's go in two panels.
  const show = (p: "map" | "round") => !part || part === p;
  const rounds = useMemo(() => economyOf(replay), [replay]);
  const buys = hasBuys(replay);
  const matchups = useMemo(() => buyMatchups(rounds, first), [rounds, first]);
  const now = rounds[round.index];
  const second = (1 - first) as 0 | 1;
  return (
    <div className="demo-economy">
      {!buys && show("map") && <p className="banner banner-info">{t("demos.economy.oldReplay")}</p>}
      {show("map") && (
        <section aria-labelledby="eco-buys">
          <h3 id="eco-buys" className="eyebrow">
            {buys ? t("demos.economy.buys") : t("demos.economy.money")}
          </h3>
          <EconomyBars rounds={rounds} first={first} current={round.index} nameOf={nameOf} measure={buys ? "value" : "start"} onPick={(i) => onGo(i, 0)} />
        </section>
      )}
      {show("map") && buys && (
        <section aria-labelledby="eco-money">
          <h3 id="eco-money" className="eyebrow">
            {t("demos.economy.money")}
          </h3>
          <MoneyLine rounds={rounds} first={first} nameOf={nameOf} current={round.index} />
        </section>
      )}
      {show("map") && buys && matchups.length > 0 && (
        <section aria-labelledby="eco-results">
          <h3 id="eco-results" className="eyebrow">
            {t("demos.economy.results", { team: nameOf(first) })}
          </h3>
          <div className="table-scroll">
            <table className="stats-table">
              <thead>
                <tr>
                  <th scope="col">{t("demos.economy.ourBuy", { team: nameOf(first) })}</th>
                  <th scope="col">{t("demos.economy.theirBuy", { team: nameOf(second) })}</th>
                  <th scope="col" className="num">
                    {t("demos.economy.won")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {matchups.map((m) => (
                  <tr key={`${m.ours}${m.theirs}`}>
                    <td>{t(`demos.economy.buy.${m.ours}`)}</td>
                    <td>{t(`demos.economy.buy.${m.theirs}`)}</td>
                    <td className="num mono">
                      {m.won}/{m.played} <span className="muted">({pct(m.won, m.played)})</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {show("round") && now && (
        <section aria-labelledby="eco-round">
          <h3 id="eco-round" className="eyebrow">
            {t("demos.economy.thisRound", { n: now.number })}
          </h3>
          {([first, second] as const).map((team) => {
            const tr = now.teams[team];
            return (
              <div key={team} className="eco-team">
                <p className="eco-team-head">
                  <span className={`live-side ${SIDE_CLASS[tr.side]}`}>{t(`live.sideShort.${tr.side}`)}</span> <b>{nameOf(team)}</b>
                  {tr.buy && <span className="chip chip-small">{t(`demos.economy.buy.${tr.buy}`)}</span>}
                  <span className="mono small muted">
                    {money(tr.start)}
                    {tr.value !== null && ` → ${t("demos.economy.carried", { v: money(tr.value) })}`}
                  </span>
                </p>
                {buys && (
                  <ul className="eco-players">
                    {tr.players.map((p) => (
                      <li key={p.player}>
                        <Who r={replay} i={p.player} round={round.index} numbers={numbers} />
                        <span className="mono small muted">
                          {money(p.start)} − {money(p.spent)}
                        </span>
                        <span className="eco-buys">
                          {p.buys.length ? (
                            p.buys.map((w, k) => <WeaponIcon key={`${w}${k}`} weapon={w} className="hud-item is-held" />)
                          ) : (
                            <span className="muted small">{t("demos.economy.nothing")}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

const BUY_MARK = { pistol: "P", eco: "E", force: "F", full: "$" } as const;

/** Two bars a round (side A left), coloured by the side each team played; the buy's letter above, a dot under the winner. */
function EconomyBars({
  rounds,
  first,
  current,
  nameOf,
  measure,
  onPick,
}: {
  rounds: EconomyRound[];
  first: 0 | 1;
  current: number;
  nameOf: (team: 0 | 1) => string;
  measure: "value" | "start";
  onPick: (i: number) => void;
}) {
  const { t } = useTranslation();
  const second = (1 - first) as 0 | 1;
  const val = (r: EconomyRound, team: 0 | 1) => (measure === "value" ? (r.teams[team].value ?? 0) : r.teams[team].start);
  const max = Math.max(1000, ...rounds.flatMap((r) => [val(r, 0), val(r, 1)]));
  const [box, room] = useWidth<HTMLDivElement>(480);
  // Two bars and a gap a round, as wide as fits (overtime scrolls below 12 px a round).
  const STEP = Math.max(12, Math.min(40, Math.floor((room - 8) / Math.max(1, rounds.length))));
  const GAP = STEP >= 20 ? 2 : 1;
  const W = Math.max(4, Math.floor((STEP - GAP - Math.max(2, STEP * 0.25)) / 2));
  const H = 120;
  const width = rounds.length * STEP;
  const y = (v: number) => H - (v / max) * H;
  return (
    <figure className="eco-chart">
      <div className="eco-legend small">
        <span>
          <i className="eco-swatch is-a" />
          {t("demos.economy.leftBar", { team: nameOf(first) })}
        </span>
        <span>
          <i className="eco-swatch is-b" />
          {t("demos.economy.rightBar", { team: nameOf(second) })}
        </span>
        <span className="muted">{t("demos.economy.legend")}</span>
      </div>
      <div className="eco-scroll" ref={box}>
        <svg viewBox={`-4 -16 ${width + 8} ${H + 36}`} width={width + 8} height={H + 36} role="img" aria-label={t("demos.economy.chartAria")}>
          {[0.5, 1].map((f) => (
            <line key={f} x1={0} x2={width} y1={y(max * f)} y2={y(max * f)} className="eco-grid" />
          ))}
          <text x={0} y={y(max) - 4} className="eco-axis">
            {money(Math.round(max / 1000) * 1000)}
          </text>
          {rounds.map((r, i) => {
            const x = i * STEP;
            const swap = i > 0 && r.teams[0].side !== rounds[i - 1].teams[0].side;
            return (
              <g key={r.round} className={`eco-round${i === current ? " is-current" : ""}`} onClick={() => onPick(i)}>
                {swap && <line x1={x - 5} x2={x - 5} y1={-12} y2={H + 4} className="eco-half" />}
                <rect x={x - 3} y={-14} width={STEP - 2} height={H + 34} className="eco-hit" />
                {([first, second] as const).map((team, k) => {
                  const tr = r.teams[team];
                  const v = val(r, team);
                  const bx = x + k * (W + GAP);
                  return (
                    <g key={team}>
                      <rect x={bx} y={y(v)} width={W} height={Math.max(1, H - y(v))} rx={3} className={`eco-bar ${SIDE_CLASS[tr.side]}`}>
                        <title>{`${t("demos.viewer.round", { n: r.number, of: rounds.length })} · ${nameOf(team)} (${t(`live.sideShort.${tr.side}`)}): ${money(v)}${tr.buy ? ` · ${t(`demos.economy.buy.${tr.buy}`)}` : ""}${tr.won ? ` · ${t("demos.economy.wonRound")}` : ""}`}</title>
                      </rect>
                      {tr.buy && W >= 6 && (
                        <text x={bx + W / 2} y={y(v) - 3} className="eco-mark">
                          {BUY_MARK[tr.buy]}
                        </text>
                      )}
                      {tr.won && <circle cx={bx + W / 2} cy={H + 7} r={3} className={`eco-win ${SIDE_CLASS[tr.side]}`} />}
                    </g>
                  );
                })}
                {labelled(i, STEP) && (
                  <text x={x + W + GAP / 2} y={H + 19} className="eco-axis is-round">
                    {r.number}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </figure>
  );
}

/** Each team's money at the start of each round: one line a team, side A solid. */
function MoneyLine({ rounds, first, nameOf, current }: { rounds: EconomyRound[]; first: 0 | 1; nameOf: (team: 0 | 1) => string; current: number }) {
  const { t } = useTranslation();
  const second = (1 - first) as 0 | 1;
  const max = Math.max(1000, ...rounds.flatMap((r) => [r.teams[0].start, r.teams[1].start]));
  const [box, room] = useWidth<HTMLDivElement>(480);
  const STEP = Math.max(12, Math.min(40, Math.floor((room - 46) / Math.max(1, rounds.length - 1))));
  const H = 90;
  const width = Math.max(1, rounds.length - 1) * STEP;
  const pt = (i: number, v: number) => `${i * STEP},${H - (v / max) * H}`;
  return (
    <figure className="eco-chart">
      <div className="eco-legend small">
        <span>
          <i className="eco-line is-a" />
          {nameOf(first)}
        </span>
        <span>
          <i className="eco-line is-b" />
          {nameOf(second)}
        </span>
      </div>
      <div className="eco-scroll" ref={box}>
        <svg viewBox={`-6 -14 ${width + 40} ${H + 30}`} width={width + 40} height={H + 30} role="img" aria-label={t("demos.economy.moneyAria")}>
          <line x1={0} x2={width} y1={0} y2={0} className="eco-grid" />
          <text x={0} y={-4} className="eco-axis">
            {money(Math.round(max / 1000) * 1000)}
          </text>
          <line x1={current * STEP} x2={current * STEP} y1={0} y2={H} className="eco-now" />
          {([first, second] as const).map((team, k) => (
            <g key={team}>
              <polyline points={rounds.map((r, i) => pt(i, r.teams[team].start)).join(" ")} className={`eco-money ${k ? "is-b" : "is-a"}`} />
              {rounds.map((r, i) => (
                <circle key={i} cx={i * STEP} cy={H - (r.teams[team].start / max) * H} r={STEP >= 20 ? 4 : 3} className={`eco-dot ${k ? "is-b" : "is-a"}`}>
                  <title>{`${t("demos.viewer.round", { n: r.number, of: rounds.length })} · ${nameOf(team)}: ${money(r.teams[team].start)}`}</title>
                </circle>
              ))}
            </g>
          ))}
          {rounds.map(
            (r, i) =>
              labelled(i, STEP) && (
                <text key={i} x={i * STEP} y={H + 14} className="eco-axis is-round">
                  {r.number}
                </text>
              ),
          )}
        </svg>
      </div>
    </figure>
  );
}
